<template>
    <button
        v-if="!isUnavailable"
        :disabled="disabled"
        @click="selectClick"
        @keyup.space="select"
        :aria-label="ariaLabelText"
        role="radio"
        :aria-checked="isSelected"
        :data-code="code"
        :data-url="imageOverride || standardImage"
        class="selection-card"
        :class="[
            { 'selection-card--selected': isSelected },
            { 'selection-card--placeholder-component': hasCrossedPlaceholder && component && component.placeholder },
        ]"
    >
        <div
            v-if="useBrandedBackground"
            class="selection-card__background"
            :style="{ backgroundImage: `url(${useBrandedBackground})` }"
        />

        <div
            v-if="standardImage || imageOverride"
            :class="{ unavailable: isOutOfStock }"
            class="selection-card__img"
            :style="{ backgroundImage: `url(${loadedImage})` }"
        />

        <img
            v-if="isPreorder"
            src="../../assets/icons/badge-preorder.svg"
            :alt="$t('PREORDER')"
            class="selection-card__badge"
        />

        <img
            v-else-if="badgeImage"
            :src="badgeImage"
            :alt="badgeAltText"
            class="selection-card__badge"
        >

        <div
            v-if="isBranded && allowBrandedActual"
            :title="isBrandedTitle"
            class="selection-card__brand"
            :style="{ backgroundImage: `url(${isBranded})`, width: brandBadgeWidth, height: brandBadgeHeight }"
        />

        <selection-card-oos-overlay
            v-if="isOutOfStock"
        />

        <!-- Indicator -->
        <span
            v-if="hasSubMenuItems"
            class="selection-card__indicator"
        >
            {{ count }}
        </span>

        <inspect-modal-trigger
            v-if="hasInspect"
            :aria-label-text="ariaLabelText"
            :component="component"
        />
    </button>
</template>

<style lang="scss">
    @import '../../style/variables';

    #cz2.cz2 {
        .selection-card {
            position: relative;
            border-radius: 8px;

            aspect-ratio: 1 / 1;
            display: flex;
            align-items: center;
            background-color: color(v2BlueLight);
            transition: border-radius $transition-fast ease-out; // Keep the border-radius transition

            &:disabled {
                cursor: not-allowed;
            }

            &:focus-visible {
                outline: none;
                border-radius: 0;

                &:after {
                    content: '';

                    position: absolute;
                    left: -4px;
                    top: -4px;

                    width: calc(100% + 7px);
                    height: calc(100% + 7px);

                    border: 4px solid #fff;
                    border-radius: 8px;
                }
            }

            &.selection-card--selected {
                animation: selectAnimation 0.2s ease-out forwards;
                border-radius: 0;

                &:after {
                    content: '';
                    pointer-events: none;

                    position: absolute;
                    left: -5px;
                    top: -5px;

                    width: calc(100% + 10px);
                    height: calc(100% + 10px);

                    border: 5px solid color(secondaryYellow);
                    border-radius: 8px;
                }

                &:focus-visible {
                    &:after {
                        border: 5px solid #fff;
                    }
                }
            }

            .selection-card__inspect {
                position: absolute;
                top: 8px;
                right: 8px;

                height: 35px;
                width: 35px;
                background-image: url(../../assets/icons/magnifying-glass.svg);
            }
        }

        .selection-card__new {
            position: absolute;
            top: -10px;
            left: -8px;
            z-index: 1;

            width: 35%;
        }

        .selection-card__badge {
            position: absolute;
            top: 0;
            left: 8px;
            width: 24px;
            height: auto;
        }

        .selection-card__brand {
            position: absolute;
            bottom: 0;
            right: 0;

            width: 64px;
            height: 64px;

            background: transparent;
            background-position: left bottom;
            background-size: contain;
            background-repeat: no-repeat;
        }

        .selection-card__img {
            position: absolute;

            // We are going to use 2x images even on a 1x monitor to make lives easier in the future.
            width: 100%;
            height: 100%;

            background: transparent;
            background-position: center;
            background-size: contain;
            background-repeat: no-repeat;

            border-radius: 8px;

            &.unavailable {
                opacity: 50%;
            }
        }

        .selection-card__background {
            position: absolute;

            left: 0;
            top: 0;
            width: 100%;
            height: 100%;

            background: transparent;
            background-position: center;
            background-size: cover;
            background-repeat: no-repeat;

            border-radius: 8px;
        }

        .selection-card--placeholder-component {
            .selection-card__img {
                height: calc(100% - 60px);
            }
        }

        .selection-card__indicator {
            position: absolute;
            top: 8px;
            right: 8px;
            height: 35px;
            min-width: 35px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            @include font(formRegular);
            color: color(primaryWhite);
            background-color: color(v2BlueMedium);
        }

        @include respond-above(xl) {
            .selection-card--placeholder-component {
                .selection-card__img {
                    height: calc(100% - 100px);
                }
            }

            .selection-card__indicator {
                height: 45px;
                min-width: 45px;

                font-size: 28px;
            }

            .selection-card__badge {
                width: 28px;
            }
        }

        @keyframes selectAnimation {
            0% {
                transform: scale(1);
            }
            50% {
                transform: scale(1.04);
            }
            100% {
                transform: scale(1);
            }
        }
    }
    #cz2.cz2.cz2__is-kiosk {
        .selection-card {
            &.selection-card--selected {
                &:after {
                    border: 10px solid color(secondaryYellow);
                    border-radius: 15px;
                }
            }
        }

        .selection-card__badge {
            width: 40px;
            height: auto;
        }

        .selection-card__inspect {
            top: 16px;
            right: 10px;

            width: 54px;
            height: 54px;
        }

        .selection-card__brand {
            width: 128px;
            height: 128px;
        }
    }
</style>

<script>
    import * as constants from '../../constants';

    import placeholderImage from '../../assets/icons/placeholder-selection-icon.svg';

    import getAsBase64 from './getAsBase64';
    import InspectModalTrigger from '../InspectModalTrigger.vue';
    import SelectionCardOosOverlay from './SelectionCardOosOverlay.vue';

    import { getAtsRecord } from '../../helpers/productData';

    // Blank image to show while loading. Should be the same dimensions are tile images (square now).
    // eslint-disable-next-line
    const empty = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

    export default {
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            isSelected: {
                type: Boolean,
                default: false,
            },
            isUnavailable: {
                type: Boolean,
                default: false,
            },
            isOutOfStock: {
                type: Boolean,
                default: false,
            },
            name: {
                type: String,
                default: null,
            },
            image: {
                type: String,
                default: null,
            },
            exactImage: {
                type: Boolean,
                default: false,
            },
            allowBranded: {
                type: Boolean,
                default: true,
            },
            count: {
                type: Number,
                default: -1,
            },
            code: {
                type: String,
                default: '',
            },
            component: {
                type: Object,
                default: null,
            },
            isPoster: {
                type: Boolean,
                default: false,
            },
            isSubMenu: {
                type: Boolean,
                default: false,
            },
        },

        components: {
            InspectModalTrigger,
            SelectionCardOosOverlay,
        },

        data() {
            return {
                loadedImage: empty,
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            selectedLashes() {
                if (this.viewController == null) {
                    return null;
                }

                const head = this.viewController.selectedAtPlacement('POPHead');

                // If there is no head... yet... use the same style as the body type.
                if (head.placeholder) {
                    return null;
                }

                return this.$store.getters.selectedLashes;
            },

            selectedColor() {
                const color = this.$store.getters.selectedColor;

                return color != null && color.custom['fun-skin-number'];
            },

            selectedBody() {
                if (this.viewController == null) {
                    return null;
                }

                const body = this.viewController.selectedAtPlacement('POPBody');

                if (body.code.includes('Female')) {
                    return 'FM';
                }

                if (body.code.includes('Baby')) {
                    return 'Baby';
                }

                return 'ML';
            },

            selectedSwatchColor() {
                return this.$store.state.customizer.selectedSwatchColors[this.currentGroupName];
            },

            /**
             * Does this component has a crossed-out placeholder image.
             * @return {Boolean} [description]
             */
            hasCrossedPlaceholder() {
                if (this.component != null) {
                    const classification = this.component.custom['fun-classification'];

                    if (classification === 'hair') {
                        // Hair uses "bald" pops as placeholders.
                        return false;
                    }

                    if (classification === 'glasses') {
                        // Glasses too.
                        return false;
                    }

                    if (classification === 'beard') {
                        // Beards too.
                        return false;
                    }

                    if (this.component.placeholder) {
                        // Otherwise use a placeholder.
                        return true;
                    }
                }

                return false;
            },

            imageOverride() {
                if (this.exactImage) {
                    return null;
                }

                if (this.component != null) {
                    let sku = this.component.custom['fun-sku'];

                    if (sku == null) {
                        sku = this.component.custom['fun-shadow-sku'];
                    }

                    if (sku == null) {
                        sku = 'Blank';
                    }

                    const body = this.selectedBody;

                    let gender = null;
                    if (this.selectedLashes == null) {
                        gender = this.selectedBody;
                    } else {
                        gender = this.selectedLashes ? 'FM' : 'ML';
                    }
                    let color = this.selectedColor;

                    if (this.hasCrossedPlaceholder && this.component.placeholder) {
                        return placeholderImage;
                    }

                    const classification = this.component.custom['fun-classification'];

                    // Skin tone buttons, use the currently select Body type to pick a render.
                    if (classification === 'tone') {
                        // We don't have a color selected yet so grab the skin type from the component.
                        color = this.component.custom['fun-skin-number'];

                        if (body === 'Baby') {
                            gender = 'Baby';
                        }

                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v3-Color-${body}-${gender}-Skin${color}`);
                    }

                    // Head aka Lashes.
                    // Reuse the Bald head (aka HeadBlank placeholder) icon.
                    if (classification === 'head') {
                        // If baby, heads are a special case.
                        if (this.component.tags.includes('baby')) {
                            // It is essentially hair.
                            return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}-Skin${color}`);
                        }

                        gender = this.component.tags.includes('lashes') ? 'FM' : 'ML';

                        // We don't have a selection yet (or the selection is changing). Grab the type from the component.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-Blank-${body}-${gender}-Skin${color}`);
                    }

                    // Nothing fancy for legs.
                    if (classification === 'legs') {
                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}`);
                    }

                    // Or torso...
                    if (classification === 'torso') {
                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}`);
                    }

                    // Or accessories...
                    if (classification === 'accessory') {
                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}`);
                    }

                    // Or pets...
                    if (classification === 'pet') {
                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}`);
                    }

                    // Glasses need special handling since "Blank" component is 3/4th view.
                    if (classification === 'glasses') {
                        if (sku === 'Blank') {
                            sku = 'BlankGlasses';
                        }

                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}-${body}-${gender}-Skin${color}`);
                    }

                    // And so are beards.
                    if (classification === 'beard') {
                        if (sku === 'Blank') {
                            sku = 'BlankGlasses';
                        }

                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}-${body}-${gender}-Skin${color}`);
                    }

                    if (classification === 'hair' || classification === 'hat') {
                        // Override with body specific skin tone preview.
                        return this.formatIconImageUrl(`${this.imageRootUrl()}CZ-Icon-v2-${sku}-${body}-${gender}-Skin${color}`);
                    }
                }

                return null;
            },

            standardImage() {
                return this.formatIconImageUrl(this.image);
            },

            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            currentGroupName() {
                return this.currentGroup.name;
            },

            ariaLabelText() {
                if (this.component?.placeholder) {
                    return `${this.$t('MODEL_WITHOUT')} ${this.currentGroupName}`;
                }

                return (this.name ?? '').replace(' - N/A', '');
            },

            useBrandedBackground() {
                if (this.allowBranded) {
                    if (this.component && this.component.custom?.['fun-brand-background']) {
                        return this.component && this.component.custom?.['fun-brand-background'];
                    }
                }

                return null;
            },

            isBranded() {
                if (this.isPoster) {
                    if (this.component && this.component.custom?.['fun-brand-badge-poster']) {
                        return this.component.custom?.['fun-brand-badge-poster'];
                    }
                }

                if (this.isSubMenu) {
                    if (this.component && this.component.custom?.['fun-brand-badge-inside']) {
                        return this.component.custom?.['fun-brand-badge-inside'];
                    }

                    return false;
                }

                if (this.component && this.component.custom?.['fun-brand-badge']) {
                    return this.component.custom?.['fun-brand-badge'];
                }

                return false;
            },

            brandBadgeWidth() {
                if (this.isBranded) {
                    if (this.component && this.component.custom?.['fun-brand-badge-size']?.length > 0) {
                        if (/^\d+$/.test(this.component.custom?.['fun-brand-badge-size'])) {
                            return `${this.component.custom?.['fun-brand-badge-size']}px`;
                        }
                    }
                }

                return null;
            },

            brandBadgeHeight() {
                if (this.isBranded) {
                    if (this.component && this.component.custom?.['fun-brand-badge-size']?.length > 0) {
                        if (/^\d+$/.test(this.component.custom?.['fun-brand-badge-size'])) {
                            return `${this.component.custom?.['fun-brand-badge-size']}px`;
                        }
                    }
                }

                return null;
            },

            isBrandedTitle() {
                if (this.component && this.component.custom?.['fun-brand-badge-title']) {
                    return this.component.custom?.['fun-brand-badge-title'];
                }

                return false;
            },

            badgeImage() {
                return this.component?.custom?.['fun-badge-image'];
            },

            badgeAltText() {
                const key = this.component?.custom?.['fun-badge-alt-text'];

                if (key?.length > 0) {
                    if (this.$te(key) || this.$te(key, this.$i18n.fallbackLocale)) {
                        return this.$t(key);
                    }

                    return key;
                }

                return this.component?.custom?.['fun-badge-alt-text'] || '';
            },

            hasInspect() {
                const isInspectStep = this.currentGroupName.includes('Accessory');
                const isSelected = !this.component?.placeholder && this.isSelected;

                return isInspectStep && isSelected && !this.hasSubMenuItems;
            },

            isSubMenuOpen() {
                return this.$store.state.customizer.isSubMenuOpen;
            },

            isHairStep() {
                return constants.HAIR_STEPS.includes(this.currentGroup.code);
            },

            hasSubMenu() {
                const groupHasSubMenu = this.currentGroup.custom?.['fun-color-sub-menu'] === 'true';

                const componentHasSubMenu = this.component?.custom?.['fun-color-sub-menu'] === 'true';

                return (!this.isHairStep || this.component.custom['fun-classification'] === 'hat') && (groupHasSubMenu || componentHasSubMenu);
            },

            hasSubMenuItems() {
                return this.hasSubMenu && this.count !== -1 && this.count > 1;
            },

            inventoryRecords() {
                return this.$store.state.ecommerce.products;
            },

            alwaysAvailable() {
                return this.$store.state.ecommerce.options.alwaysAvailable;
            },

            inventory() {
                if (this.component) {
                    if (this.alwaysAvailable) {
                        return null;
                    }

                    const productByLocation = getAtsRecord(this.$store.state, this.component, this.viewController);

                    if (productByLocation) {
                        return productByLocation;
                    }

                    // Kiosk uses internal data.
                    // Does it have a SKU?
                    if (this.inventoryRecords && this.component?.custom?.['fun-sku']) {
                        const product = this.inventoryRecords[this.component.custom['fun-sku']];

                        if (product) {
                            return product;
                        }
                    }

                    if (this.inventoryRecords && this.component?.custom?.['fun-shadow-sku']) {
                        const product = this.inventoryRecords[this.component.custom['fun-shadow-sku']];

                        if (product) {
                            return product;
                        }
                    }
                }

                return null;
            },

            isPreorder() {
                const { inventory } = this;

                if (inventory == null) {
                    return false;
                }

                if (inventory != null && inventory.ats === 0 && inventory.ato > 0) {
                    return true;
                }

                return false;
            },

            allowBrandedActual() {
                if (this.allowBranded) {
                    return true;
                }

                if (this.isSubMenu && this.isBranded) {
                    return true;
                }

                return false;
            },
        },

        watch: {
            standardImage() {
                this.load();
            },

            imageOverride() {
                this.load();
            },
        },

        mounted() {
            this.load();
        },

        methods: {
            imageRootUrl() {
                return this.viewController.blueprint.imageRootUrl;
            },

            formatIconImageUrl(link) {
                if (link == null) {
                    return null;
                }

                let url = link;

                // Normalize protocol relative URLs, left over from the ETl.
                if (url.startsWith('//')) {
                    url = `https:${url}`;
                }

                let cdnPrefix = 'https://cdn';

                if (this.viewController?.blueprint?.custom?.['fun-cdn-prefix']) {
                    cdnPrefix = this.viewController?.blueprint?.custom?.['fun-cdn-prefix'];
                }

                // Switch to the new CDN.
                url = url.replace('https://api', cdnPrefix);

                // Or enforce API.
                if (this.$store.state.customizer.options.noCdn) {
                    url = url.replace('https://cdn', 'https://api');
                }

                // Remove any extra parameters left from the old ETL.
                const queryStringIndex = url.indexOf('?');
                if (queryStringIndex > 0) {
                    url = url.substr(0, queryStringIndex);
                }

                // Enforce transparent webp so we don't have to deal with color mismatches.
                // Also better for the file size.
                // qlt=30 is pretty aggressive.
                url += '?fmt=webp&qlt=40';

                // We have rendered everything at high res. Choose the desired icon size.
                url += '&rwid=562&rhei=562';

                return url;
            },

            selectClick($event) {
                // Check for events that were cancelled by Swiper.
                // Use case:
                //    Touch start
                //    Swipe (keep finger on the card)
                //    Wait for animations to stop
                //    Release touch.
                // iOS usually cancels the touch event while Swiper did not out of the box.
                if (!$event.defaultPrevented) {
                    // Allow select if OOS component has sub-menu
                    if ((!this.isOutOfStock) || (this.isOutOfStock && this.hasSubMenu)) {
                        this.select();
                    }
                }
            },

            select() {
                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Select component ${this.code}`,
                });

                this.$emit('select');
            },

            load() {
                if (this.debounce) {
                    clearTimeout(this.debounce);
                }

                this.debounce = setTimeout(() => this.loadDebounced());
            },

            loadDebounced() {
                if (this.imageOverride) {
                    getAsBase64(this.imageOverride).then((img) => {
                        this.loadedImage = img;
                    }).catch(() => {
                        getAsBase64(this.standardImage).then((img) => {
                            this.loadedImage = img;
                        }).catch(() => {
                            this.loadedImage = empty;
                        });
                    });
                } else {
                    getAsBase64(this.standardImage).then((img) => {
                        this.loadedImage = img;
                    }).catch(() => {
                        this.loadedImage = empty;
                    });
                }
            },
        },
    };
</script>
